import { Fragment } from 'react';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { Company, Registration } from '@legacy-types';

import { userHasPermission as p } from '@legacy-libs/utils';
import CompanyProvider from '@legacy-providers/company/CompanyProvider';
import RegistrationProvider from '@legacy-providers/registration/RegistrationProvider';
import { selectUser } from '@legacy-redux/auth/selectors';
import { PREFERENCES_ACTIONS } from '@legacy-redux/preferences/actions';
import { selectCompany, selectCompanyRegistration, selectEvent } from '@legacy-redux/preferences/selectors';
import { selectRegistrations } from '@legacy-redux/registrations/selectors';

import { Layout } from '@shared/layout/components/Layout';
import { Sidebar } from '@shared/layout/components/Sidebar';
import { Alert } from '@shared/ui/components/Alert';

import { CompanyHeader } from '@company/layout/CompanyHeader';
import { CompanyMenu } from '@company/layout/CompanyMenu';
import { CompanyRoutes } from '@company/layout/CompanyRoutes';
import { SecurityQuestionsWarnings } from '@company/layout/SecurityQuestionsWarnings';

export function CompanyLayout() {
  const dispatch = useDispatch();

  const history = useHistory();
  const location = useLocation();

  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const registration = useSelector(selectCompanyRegistration);
  const event = useSelector(selectEvent);

  function setPreference(key: string, value: unknown) {
    dispatch(PREFERENCES_ACTIONS.setPreference(key, value));
  }

  // TODO: Check if selectedEvent preference is missing or empty...

  function updatePreferences(company: Company) {
    console.log('Setting company and event (updatePreferences)...', company);

    const registration = company.registrations[company.registrations.length - 1] || null;

    setPreference('company', company.id);

    if (registration) {
      setPreference('selectedEvent', registration.event);
      setPreference('companyEvent', registration.event.id);
      setPreference('companyRegistration', registration.id);
    }
  }

  function setRegistration(registration: Registration) {
    console.log('Setting event and registration and reloading...');
    setPreference('company', registration.companyId);
    setPreference('companyEvent', registration.event.id);
    setPreference('selectedEvent', registration.event);
    setPreference('companyRegistration', registration.id);
  }

  function onSelectCompany(company: Company) {
    updatePreferences(company);

    visitDashboard();
  }

  function onSelectRegistration(registration: Registration) {
    setRegistration(registration);

    visitDashboard();
  }

  function visitDashboard() {
    if (location.pathname !== '/dashboard') {
      history.push('/dashboard');
    }
  }

  useEffect(() => {
    // If we have no company selected but user has companies
    if (!company && user.companies.length) {
      const companiesWithRegistrations = user.companies.filter((c: any) => c.registrations.length);

      if (companiesWithRegistrations.length) {
        updatePreferences(companiesWithRegistrations[companiesWithRegistrations.length - 1]);

        return; // Company will be set, which will trigger this effect again
      }
    }

    // If we have a company but no registration selected
    if (company && !registration) {
      if (company.registrations.length) {
        updatePreferences(company);
      } else {
        // Find another company with registrations
        const companyWithRegistrations = user.companies.find((c: any) => c.registrations.length);

        if (companyWithRegistrations) {
          updatePreferences(companyWithRegistrations);
        }
      }
    }
  }, [company, registration, user]);

  if (company && registration && !event?.id) {
    // If currentEvent in preferences is corrupted and set to `{}` or `null`
    console.error('Company and registration set but current event is null or empty... Hard refresh.');
    updatePreferences(company);
    window.location.reload();
  }

  if (!registration) {
    console.warn('Company view loaded but no registration found...');

    return null;
  }

  return (
    <CompanyProvider>
      <RegistrationProvider>
        <Layout
          homeUrl="/dashboard"
          header={
            <CompanyHeader
              company={company ?? null}
              companies={user.companies.filter((c: any) => c.registrations.length)}
              registration={registration}
              registrations={company?.registrations ?? []}
              onCompanyChange={onSelectCompany}
              onRegistrationChange={onSelectRegistration}
            />
          }
          sidebar={
            <Sidebar
              sectionLink={
                p(user, ['venues.venues'])
                  ? {
                      label: 'Go to admin',
                      href: '/admin',
                    }
                  : undefined
              }
            >
              <CompanyMenu />
            </Sidebar>
          }
          main={
            <Fragment key={registration?.id}>
              <SecurityQuestionsWarnings className="mt-8" />
              <ErrorBoundary
                fallback={
                  <Alert variant="destructive" title="Something went wrong..." className="my-4">
                    Something went wrong. Please try refreshing or selecting a different event or company.
                  </Alert>
                }
              >
                <CompanyRoutes />
              </ErrorBoundary>
            </Fragment>
          }
        />
      </RegistrationProvider>
    </CompanyProvider>
  );
}
